import React, { useState, useEffect, useRef } from 'react';

import './TeamDeliveryRocket.css';

const rocketAreas = [
    {
        id: 'discovery',
        label: 'DISCOVERY',
        title: 'Discovery steers the rocket',
        description:
            'Continuous discovery is what gives the team direction. Our OKRs define the outcomes we are working toward — discovery is how we figure out the best way to get there.',
        ledBy: 'Whole team — PM & Designer led',
        image: '/img/explore-and-prioritize-uten-tekst.png',
        color: '#010A59',
        textColor: '#ffffff',
    },
    {
        id: 'product-improvements',
        label: 'PRODUCT IMPROVEMENTS',
        title: 'Product improvements',
        description:
            'From removing friction to adding new capabilities — product improvements are driven by validated insights from discovery and deliver direct customer value.',
        ledBy: 'PM & Designer led',
        image: '/img/productmanager.svg',
        color: '#F9E6B5',
        textColor: '#010A59',
    },
    {
        id: 'technical-improvements',
        label: 'TECHNICAL IMPROVEMENTS',
        title: 'Technical improvements',
        description:
            'We continuously invest in the technical foundation. These improvements have customer value on par with product changes — faster, safer, more scalable software benefits everyone.',
        ledBy: 'Tech Lead led',
        image: '/img/Figur-18-Tech Lead-outline-RGB.svg',
        color: '#F9E6B5',
        textColor: '#010A59',
    },
    {
        id: 'stability',
        label: 'STABILITY & RELIABILITY',
        title: 'Stability & reliability',
        description:
            "Day-to-day bugs, performance and maintenance keep the rocket airborne. Safe, observable and deployable changes are everyone's responsibility.",
        ledBy: 'Whole team',
        image: '/img/Figur-17-Product Specialist-outline-RGB.svg',
        color: '#F9E6B5',
        textColor: '#010A59',
    },
    {
        id: 'delivery',
        label: 'CONTINUOUS DELIVERY',
        title: 'Continuous delivery',
        description:
            'Everything ships incrementally to production. AI lets us move faster without cutting corners — from code generation to automated testing. Done means live and verified.',
        ledBy: 'Whole team',
        image: '/img/Figur-19-Team Lead-outline-RGB.svg',
        color: '#0A41FA',
        textColor: '#ffffff',
    },
];

// Layout constants
const BL  = 105;   // body left x
const BR  = 295;   // body right x
const CX  = 200;   // center x
const FIN_L = 45;  // fin outer-left x
const FIN_R = 355; // fin outer-right x
const BODY_TOP  = 145; // y where body bands start (base of nose cone)
const FIN_START = 325; // y where fins start
const FIN_BOT   = 550; // y where fins end

// Mid-Y of each segment as a fraction of the SVG viewBox height (720)
const SEGMENT_Y_FRAC = {
    discovery:                72  / 720,
    'product-improvements':   207 / 720,
    'technical-improvements': 338 / 720,
    stability:                480 / 720,
    delivery:                 574 / 720,
};

function RocketSVG({ activeId, onAreaClick, svgRef }) {
    const bodySegments = [
        { id: 'product-improvements',   yTop: BODY_TOP, yBottom: 265 },
        { id: 'technical-improvements', yTop: 265,      yBottom: 410 },
        { id: 'stability',              yTop: 410,      yBottom: FIN_BOT },
    ];

    const LABEL_LINES = {
        'ROADMAP':                ['ROADMAP'],
        'PRODUCT IMPROVEMENTS':   ['PRODUCT', 'IMPROVEMENTS'],
        'TECHNICAL IMPROVEMENTS': ['TECHNICAL', 'IMPROVEMENTS'],
        'STABILITY & RELIABILITY':['STABILITY &', 'RELIABILITY'],
        'CONTINUOUS DELIVERY':    ['CONTINUOUS', 'DELIVERY'],
    };

    function LabelText({ label, x, midY, fill, fontSize: fs }) {
        const lines = LABEL_LINES[label] || [label];
        const lineH = fs * 1.35;
        const startY = lines.length === 1 ? midY : midY - lineH / 2;
        return (
            <text x={x} textAnchor='middle' fill={fill} fontSize={fs}
                  fontFamily='Rubik, sans-serif' fontWeight='700' letterSpacing='0.1em'
                  style={{ pointerEvents: 'none', userSelect: 'none' }}>
                {lines.map((line, i) => (
                    <tspan key={i} x={x} dy={i === 0 ? startY - midY + midY : lineH}
                           dominantBaseline='middle'>{line}</tspan>
                ))}
            </text>
        );
    }

    function handleClick(id) { onAreaClick(id); }

    const txtProps = {
        textAnchor: 'middle', dominantBaseline: 'middle',
        fontFamily: 'Rubik, sans-serif', fontWeight: '700',
        letterSpacing: '0.1em', style: { pointerEvents: 'none', userSelect: 'none' },
    };

    return (
        <svg
            ref={svgRef}
            viewBox='0 0 400 720'
            xmlns='http://www.w3.org/2000/svg'
            className='rocket-svg'
            aria-label='Team delivery rocket'
            role='img'
        >
            {/* ── DISCOVERY ── */}
            <g
                className={`rocket-segment${activeId === 'discovery' ? ' rocket-segment--active' : ''}`}
                onClick={() => handleClick('discovery')}
                role='button' aria-label='DISCOVERY' aria-pressed={activeId === 'discovery'}
                tabIndex={0}
                onKeyDown={e => (e.key==='Enter'||e.key===' ') && (e.preventDefault(), handleClick('discovery'))}
            >
                <polygon
                    points={`${CX},0 ${BR},${BODY_TOP} ${BL},${BODY_TOP}`}
                    fill='#010A59' stroke='#010A59' strokeWidth='1'
                />
                <polygon
                    points={`${BL},${FIN_START} ${FIN_L},${FIN_BOT} ${BL},${FIN_BOT}`}
                    fill='#6B82C4' stroke='#6B82C4' strokeWidth='1'
                />
                <polygon
                    points={`${BR},${FIN_START} ${FIN_R},${FIN_BOT} ${BR},${FIN_BOT}`}
                    fill='#6B82C4' stroke='#6B82C4' strokeWidth='1'
                />
                <text x={CX} y={BODY_TOP - 30} fill='#ffffff' fontSize={15} {...txtProps}>
                    DISCOVERY
                </text>
            </g>

            {/* ── BODY BANDS ── */}
            {bodySegments.map(seg => {
                const area    = rocketAreas.find(a => a.id === seg.id);
                const isActive = activeId === seg.id;
                const fill    = area ? area.color     : '#F9E6B5';
                const color   = area ? area.textColor : '#010A59';
                const midY    = (seg.yTop + seg.yBottom) / 2 + 4;

                return (
                    <g
                        key={seg.id}
                        className={`rocket-segment${isActive ? ' rocket-segment--active' : ''}`}
                        onClick={() => handleClick(seg.id)}
                        role='button' aria-label={area?.label} aria-pressed={isActive}
                        tabIndex={0}
                        onKeyDown={e => (e.key==='Enter'||e.key===' ') && (e.preventDefault(), handleClick(seg.id))}
                    >
                        <rect x={BL} y={seg.yTop} width={BR - BL} height={seg.yBottom - seg.yTop}
                              fill={fill} stroke='#11175C' strokeWidth='2' />
                        <LabelText label={area?.label ?? seg.id.toUpperCase()} x={CX} midY={midY} fill={color} fontSize={15} />
                    </g>
                );
            })}

            {/* ── DELIVERY ── */}
            {(() => {
                const isActive = activeId === 'delivery';
                const d = `M ${BL} ${FIN_BOT} L ${BR} ${FIN_BOT} L ${BR} 595 L 260 595 L 270 720 L ${CX} 655 L 130 720 L 140 595 L ${BL} 595 Z`;
                return (
                    <g
                        className={`rocket-segment${isActive ? ' rocket-segment--active' : ''}`}
                        onClick={() => handleClick('delivery')}
                        role='button' aria-label='CONTINUOUS DELIVERY' aria-pressed={isActive}
                        tabIndex={0}
                        onKeyDown={e => (e.key==='Enter'||e.key===' ') && (e.preventDefault(), handleClick('delivery'))}
                    >
                        <path d={d} fill='#0A41FA' stroke='#11175C' strokeWidth='1.5' />
                        <LabelText label='CONTINUOUS DELIVERY' x={CX} midY={FIN_BOT + 24} fill='#ffffff' fontSize={15} />
                    </g>
                );
            })()}
        </svg>
    );
}

function TeamDeliveryRocket() {
    const defaultArea = rocketAreas.find((a) => a.id === 'discovery');
    const [activeArea, setActiveArea] = useState(defaultArea);
    const [arrowY, setArrowY] = useState(null);
    const svgRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        function updateArrow() {
            if (!svgRef.current || !panelRef.current) return;
            const svgRect = svgRef.current.getBoundingClientRect();
            const panelRect = panelRef.current.getBoundingClientRect();
            const frac = SEGMENT_Y_FRAC[activeArea.id] ?? 0.5;
            const segAbsY = svgRect.top + svgRect.height * frac;
            const arrowTopPx = segAbsY - panelRect.top;
            const clamped = Math.max(24, Math.min(panelRect.height - 24, arrowTopPx));
            setArrowY(clamped);
        }
        updateArrow();
        window.addEventListener('resize', updateArrow);
        return () => window.removeEventListener('resize', updateArrow);
    }, [activeArea.id]);

    function handleAreaClick(id) {
        const area = rocketAreas.find((a) => a.id === id);
        if (area) setActiveArea(area);
    }

    return (
        <div className='rocket-section' id='team-delivery-rocket'>
            <div className='rocket-section__header'>
                <h1 className='rocket-section__title'>Team delivery rocket</h1>
                <p className='rocket-section__intro'>
                    A high-performing product team doesn't just build — it discovers, improves, and delivers continuously.
                    The rocket shows how we think about the different types of work, and how they fit together.
                    Click a segment to explore what each part means in practice.
                </p>
            </div>

            <div className='rocket-layout'>
                <div className='rocket-layout__svg'>
                    <RocketSVG activeId={activeArea.id} onAreaClick={handleAreaClick} svgRef={svgRef} />
                </div>

                <div className='rocket-layout__panel' ref={panelRef}>
                    {arrowY !== null && (
                        <div
                            className='rocket-panel-arrow'
                            style={{ top: arrowY }}
                        />
                    )}
                    <div className='rocket-info-panel'>
                        <p className='rocket-info-panel__label'>{activeArea.label}</p>
                        <h2 className='rocket-info-panel__title'>{activeArea.title}</h2>
                        <p className='rocket-info-panel__description'>{activeArea.description}</p>
                        <p className='rocket-info-panel__led-by'>
                            Led by &mdash; {activeArea.ledBy}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TeamDeliveryRocket;
